import { useTranslation } from 'react-i18next'

import {
  AgentVerificationRejectionReasonDropdownCodes,
  type AgentVerificationApplicationRow,
  type AgentVerificationMatchType
} from '@percent/admin-dashboard/api/types'
import { StatusVariant } from '@percent/admin-dashboard/common/components/status/Status.types'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import { AgentVerificationStatus } from '@percent/domain/verifications'
import { Badge, IconRegistry, Tooltip, Typography } from '@percent/lemonade'
import styles from './agentVerificationStatusBadges.module.scss'

type AgentVerificationStatusMap = Record<
  'rejected' | 'approved' | 'pending',
  {
    variant: StatusVariant
    message: LocaleKey
    icon: keyof typeof IconRegistry
  }
>

const statusMap: AgentVerificationStatusMap = {
  rejected: {
    variant: 'critical',
    message: 'status.rejected',
    icon: 'reject'
  },
  approved: {
    variant: 'positive',
    message: 'status.approved',
    icon: 'check-badge'
  },
  pending: {
    variant: 'default',
    message: 'status.pending',
    icon: 'clock'
  }
}

const getAVStatus = (status?: AgentVerificationStatus): keyof typeof statusMap => {
  if (
    [
      AgentVerificationStatus.PENDING,
      AgentVerificationStatus.PENDING_REVIEW,
      AgentVerificationStatus.PENDING_USER_VERIFICATION
    ].includes(status as AgentVerificationStatus)
  ) {
    return 'pending'
  }

  if (status === 'approved') {
    return 'approved'
  }

  return 'rejected'
}

export function StatusBadge({ status }: Pick<AgentVerificationApplicationRow, 'status'>) {
  const aVStatus = getAVStatus(status)
  const { t } = useTranslation()

  return (
    <Badge variant={statusMap[aVStatus].variant} icon={statusMap[aVStatus].icon}>
      {t(statusMap[aVStatus].message)}
    </Badge>
  )
}

export function RejectionBadge({ rejectionReasonCode }: Pick<AgentVerificationApplicationRow, 'rejectionReasonCode'>) {
  const { t } = useTranslation()

  if (!rejectionReasonCode) return <Typography>{t('status.na')}</Typography>

  const maxCharacterCount = 30
  const content = t(`dropdown.reject.${rejectionReasonCode}`).trim()
  const slicedContent = content.slice(0, maxCharacterCount).trim()
  const fakeElipsisContent = content.length > maxCharacterCount ? `${slicedContent}...` : slicedContent

  if (rejectionReasonCode === AgentVerificationRejectionReasonDropdownCodes.Other)
    return (
      <Tooltip content={content}>
        <div className={styles.rejectionBadgeWrapper}>
          <Badge variant="default">{fakeElipsisContent}</Badge>
        </div>
      </Tooltip>
    )

  return (
    <Tooltip content={content}>
      <div className={styles.rejectionBadgeWrapper}>
        <Badge variant="warning" icon="warning">
          {fakeElipsisContent}
        </Badge>
      </div>
    </Tooltip>
  )
}

export function MatchTypeBadge({ matchType }: { matchType: AgentVerificationMatchType }) {
  const { t } = useTranslation()

  return (
    <Badge variant={matchType === 'exact' ? 'critical' : 'warning'} icon="flag">
      {t(`typography.matchType.${matchType}`)}
    </Badge>
  )
}
